import AUTO_CAD_COLOR_INDEX from './AutoCadColorIndex';
import DxfArrayScanner, { ScannerGroup } from './DxfArrayScanner';
import { CommonDxfEntity, skipApplicationGroups } from './entities/shared';
import isMatched from './shared/isMatched';
import { parseXData } from './shared/xdata';

/**
 * Returns the truecolor value of the given AutoCad color index value
 * @param {import('../types').ColorIndex} index
 * @return {import('../types').ColorInstance} truecolor value as a number
 */
export function getAcadColor(index: number) {
    return AUTO_CAD_COLOR_INDEX[index];
}

/** Some entities may contain embedded object which is started by group 101. All the rest data until
 * end of entity should not be interpreted as entity attributes. There is no documentation for this
 * feature.
 * @param scanner
 */
export function skipEmbeddedObject(scanner: DxfArrayScanner) {
    /* Ensure proper start group. */
    scanner.rewind();
    let curr = scanner.next();
    if (curr.code !== 101) {
        throw new Error('Bad call for skipEmbeddedObject()');
    }
    do {
        curr = scanner.next();
    } while (curr.code !== 0);
    scanner.rewind();
}

/**
 * Attempts to parse codes common to all entities. Returns true if the group
 * was handled by this function.
 * @param {*} entity - the entity currently being parsed
 * @param {*} curr - the current group being parsed
 */
export function checkCommonEntityProperties(entity: CommonDxfEntity, curr: ScannerGroup, scanner: DxfArrayScanner) {
    // Use object spread to avoid modifying the function parameter directly
    const updatedEntity = { ...entity };

    if (isMatched(curr, 102)) {
        skipApplicationGroups(curr, scanner);
        return true;
    }

    switch (curr.code) {
        case 0:
            updatedEntity.type = curr.value as string;
            break;
        case 5:
            updatedEntity.handle = curr.value as string;
            break;
        case 330:
            if (!updatedEntity.ownerDictionarySoftId) {
                updatedEntity.ownerDictionarySoftId = curr.value;
            } else {
                updatedEntity.ownerBlockRecordSoftId = curr.value;
            }
            break;
        case 360:
            updatedEntity.ownerdictionaryHardId = curr.value;
            break;
        case 67:
            updatedEntity.isInPaperSpace = !!curr.value;
            break;
        case 8:
            updatedEntity.layer = curr.value;
            break;
        case 6:
            updatedEntity.lineType = curr.value;
            break;
        case 347:
            updatedEntity.materialObjectHardId = curr.value;
            break;
        case 62:
            updatedEntity.colorIndex = curr.value;
            updatedEntity.color = getAcadColor(Math.abs(curr.value));
            break;
        case 370:
            updatedEntity.lineweight = curr.value;
            break;
        case 48:
            updatedEntity.lineTypeScale = curr.value;
            break;
        case 60:
            updatedEntity.isVisible = !!curr.value;
            break;
        case 92:
            updatedEntity.proxyByte = curr.value;
            break;
        case 310:
            updatedEntity.proxyEntity = curr.value;
            break;
        case 100:
            break;
        case 420:
            updatedEntity.color = curr.value;
            break;
        case 430:
            updatedEntity.transparency = curr.value;
            break;
        case 390:
            updatedEntity.plotStyleHardId = curr.value;
            break;
        case 284:
            updatedEntity.shadowMode = curr.value;
            break;
        case 1001:
            updatedEntity.xdata = parseXData(curr, scanner);
            break;
        default:
            return false;
    }

    // Return the updated entity object instead of mutating the parameter
    return updatedEntity;
}
